import React, { FC, useCallback, useContext, useMemo } from 'react'

import { AuthContext } from '@deltek/specification-client/core/Configurations/AuthContext'
import SingleLineEllipsis from '@deltek/specification-client/core/Feedbacks/SingleLineEllipsis'
import { NavPromptMessageProps } from '@deltek/specification-client/core/Forms/NavPrompt'
import NavPromptDialogContainer from '@deltek/specification-client/core/Forms/NavPromptDialogContainer'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Theme, makeStyles } from '@material-ui/core'
import { EditOutlined } from '@material-ui/icons'
import DeleteIcon from '@material-ui/icons/Delete'
import ContentEditable from 'react-contenteditable'
import { useLocation } from 'react-router-dom'
import { PROMPT_DELETE_NOTE, TITLE_DELETE_NOTE } from './ListingNotes.constants'
import { ProductDetailNoteData } from './ListingNotes.types'
import PDNColors from './PDNColors'

const {
    viewOnlyBorderColor,
    viewOnlyHeaderColor,
    customerNoteColor,
    deltekNoteColor,
} = PDNColors
export interface ListingNotesCardProps {
    mode: string
    note: Partial<ProductDetailNoteData>
    enableEdit?: boolean
    onEdit?: (id: string) => void
    onDelete?: ({
        id,
        isTemporary,
    }: {
        id: string
        isTemporary: boolean
    }) => void
    currentUserId?: string
}

export type ListingNotesCardStyleProps = {
    mode: string
}

const useStyles = makeStyles((theme: Theme) => ({
    elementCard: {
        flex: '0 0 100%',
        width: '80vw',
        maxWidth: pxToRem(1200),
        border: ({ mode }: ListingNotesCardStyleProps) =>
            `1px solid ${
                mode === 'display' ? viewOnlyBorderColor : deltekNoteColor
            }`,
        margin: theme.spacing(1, 'auto'),
    },
    title: {
        flexWrap: 'nowrap',
        justifyContent: 'space-between',
        backgroundColor: ({ mode }: ListingNotesCardStyleProps) =>
            mode === 'display' ? viewOnlyHeaderColor : customerNoteColor,
        padding: theme.spacing(1),
        color: theme.palette.common.white,
        fontSize: pxToRem(16),
        fontWeight: 500,
    },
    nameContainer: {
        flexWrap: 'nowrap',
        justifyContent: 'flex-end',
    },
    deleteIcon: {
        marginLeft: theme.spacing(1),
        fontSize: pxToRem(20),
        cursor: 'pointer',
    },
    content: {
        height: 'auto',
        padding: theme.spacing(1),
        margin: pxToRem(10),
        fontSize: pxToRem(14),
        wordWrap: 'break-word',
    },
    reasonText: {
        marginRight: theme.spacing(1),
        minWidth: pxToRem(220),
    },
}))

export const ListingNotesCard: FC<ListingNotesCardProps> = ({
    mode,
    note,
    onEdit,
    onDelete,
    currentUserId,
    enableEdit,
}) => {
    const classes = useStyles({ mode })

    const {
        id,
        createdDate: dateAdded,
        reason,
        longDescription,
        userId,
        isTemporary,
        noteType,
        userFullName,
        userRole,
    } = note

    const location = useLocation()

    const { currentUserInfo } = useContext(AuthContext)

    const basePath = useMemo(() => {
        return location.pathname.split('/')?.[2]
    }, [location])

    const noteDescription = useMemo(() => {
        const uriHolder = '{URI}'
        let index = longDescription?.indexOf(uriHolder) ?? -1
        if (index !== -1) {
            const slash =
                longDescription?.charAt(index + uriHolder.length) === '/'
            let uri = `/bpm/${basePath}/product/listing`
            if (!slash) {
                uri = `${uri}/`
            }
            return longDescription?.replace(uriHolder, uri)
        }
        return longDescription
    }, [basePath, longDescription])

    const reasonText = useMemo(() => {
        const dateToShow = new Date(dateAdded ?? '')
        const localDateFormat: Partial<Intl.DateTimeFormatOptions> = {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        }
        let hours = dateToShow.getHours()
        let minutes = dateToShow.getMinutes()
        const ampm = hours >= 12 ? 'pm' : 'am'
        hours = hours % 12
        hours = hours ? hours : 12 // the hour '0' should be '12'
        const strTime = `${hours}:${String(minutes).padStart(2, '0')} ${ampm}`

        return `${dateToShow.toLocaleString('en-US', localDateFormat)} ${
            !!isTemporary ? '' : strTime
        } | ${reason}`
    }, [dateAdded, reason, noteType])

    const nameText = useMemo(
        () =>
            !!userFullName && !!userRole && !isTemporary
                ? `${userFullName ?? ''}, ${userRole ?? ''}`
                : `${currentUserInfo?.firstName} ${currentUserInfo?.lastName}, ${currentUserInfo?.roles[0]}`,
        [userFullName, userRole, isTemporary, currentUserInfo]
    )

    const handleEditClick = useCallback(
        (id) => () => {
            onEdit?.(id)
        },
        [id, onEdit]
    )

    const handleDeleteClick = useCallback(
        (id) => () => {
            return NavPromptDialogContainer(
                JSON.stringify({
                    title: TITLE_DELETE_NOTE,
                    preferredMessage: PROMPT_DELETE_NOTE,
                    buttonColor: deltekNoteColor,
                    confirmText: 'Yes',
                    cancelText: 'No',
                } as NavPromptMessageProps),
                (callbackState) => {
                    if (callbackState) {
                        onDelete?.({ id, isTemporary: !!isTemporary })
                    }
                }
            )
        },
        [id, isTemporary, onDelete]
    )
    const noop = useCallback(() => {}, [])

    return (
        <GridItem className={classes.elementCard}>
            <GridContainer className={classes.title} wrap="nowrap">
                <GridItem xs={7} className={classes.reasonText}>
                    <SingleLineEllipsis text={reasonText} />
                </GridItem>
                <GridItem xs={5}>
                    <GridContainer
                        className={classes.nameContainer}
                        wrap="nowrap"
                    >
                        <SingleLineEllipsis text={nameText} />
                        {userId === currentUserId &&
                            (mode !== 'display' || enableEdit) && (
                                <EditOutlined
                                    className={classes.deleteIcon}
                                    onClick={handleEditClick(id)}
                                />
                            )}
                        {userId === currentUserId &&
                            (mode !== 'display' || enableEdit) && (
                                <DeleteIcon
                                    className={classes.deleteIcon}
                                    onClick={handleDeleteClick(id)}
                                />
                            )}
                    </GridContainer>
                </GridItem>
            </GridContainer>

            <ContentEditable
                onChange={noop}
                html={(noteDescription ?? '') as string}
                className={classes.content}
                disabled={true}
                data-testid="editableContent"
            />
        </GridItem>
    )
}
